/**
 * Document sharing modal component.
 * Allows users to share documents with others and manage permissions.
 */

import React, { useState, useEffect } from 'react'
import { logError } from '@utils/logger'
import { XMarkIcon, UserPlusIcon, TrashIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { documentSharesAPI } from '@api/documentShares'
import { DocumentShare, DocumentShareCreate, PermissionLevel } from '@types/index'
import toast from 'react-hot-toast'

interface ShareDocumentModalProps {
  documentId: string
  isOpen: boolean
  onClose: () => void
  onShareUpdate?: () => void
}

const ShareDocumentModal: React.FC<ShareDocumentModalProps> = ({
  documentId,
  isOpen,
  onClose,
  onShareUpdate,
}) => {
  const [shares, setShares] = useState<DocumentShare[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [selectedPermission, setSelectedPermission] = useState<PermissionLevel>('viewer')

  useEffect(() => {
    if (isOpen && documentId) {
      loadShares()
    }
  }, [isOpen, documentId])

  const loadShares = async () => {
    setIsLoading(true)
    try {
      const documentShares = await documentSharesAPI.getDocumentShares(documentId)
      setShares(documentShares)
    } catch (error: any) {
      logError('Error loading shares:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!shareEmail.trim()) {
      return
    }

    setIsSharing(true)
    try {
      const shareData: Omit<DocumentShareCreate, 'document_id'> = {
        shared_with_email: shareEmail.trim(),
        permission_level: selectedPermission,
      }
      await documentSharesAPI.shareDocument(documentId, shareData)
      setShareEmail('')
      setSelectedPermission('viewer')
      await loadShares()
      onShareUpdate?.()
    } catch (error: any) {
      // Handle FastAPI validation errors (422) - detail is an array of error objects
      let errorMessage = 'Failed to share document'
      if (error.response?.data) {
        const errorData = error.response.data
        if (Array.isArray(errorData.detail)) {
          // Format validation errors into a readable message
          const validationErrors = errorData.detail.map((err: any) => {
            const field = err.loc && err.loc.length > 1 ? err.loc[err.loc.length - 1] : 'field'
            return `${field}: ${err.msg}`
          }).join(', ')
          errorMessage = validationErrors || 'Validation error'
        } else {
          errorMessage = errorData.detail || error.message || errorMessage
        }
      } else {
        errorMessage = error.message || errorMessage
      }
    } finally {
      setIsSharing(false)
    }
  }

  const handleRemoveShare = async (shareId: string) => {
    if (!confirm('Are you sure you want to remove this share?')) {
      return
    }

    try {
      await documentSharesAPI.removeShare(shareId)
      await loadShares()
      onShareUpdate?.()
    } catch (error: any) {
      logError('Error removing share:', error)
    }
  }

  const handleUpdatePermission = async (shareId: string, newPermission: PermissionLevel) => {
    try {
      await documentSharesAPI.updateSharePermission(shareId, newPermission)
      await loadShares()
      onShareUpdate?.()
    } catch (error: any) {
      logError('Error updating permission:', error)
    }
  }

  const handleSendReminder = async (shareId: string) => {
    try {
      await documentSharesAPI.sendReminder(shareId)
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send reminder'
      logError('Error sending reminder:', error)
    }
  }

  const getPermissionLabel = (level: PermissionLevel): string => {
    const labels: Record<PermissionLevel, string> = {
      viewer: 'Viewer',
      reviewer: 'Reviewer',
      editor: 'Editor',
      owner: 'Owner',
    }
    return labels[level]
  }

  const getPermissionDescription = (level: PermissionLevel): string => {
    const descriptions: Record<PermissionLevel, string> = {
      viewer: 'Can only view the document',
      reviewer: 'Can view and add comments',
      editor: 'Can view and edit the document',
      owner: 'Full control',
    }
    return descriptions[level]
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Share Document</h3>
              <button
                onClick={onClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Share form */}
            <form onSubmit={handleShare} className="mb-6">
              <div className="flex space-x-2">
                <div className="flex-1">
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    required
                  />
                </div>
                <select
                  value={selectedPermission}
                  onChange={(e) => setSelectedPermission(e.target.value as PermissionLevel)}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                >
                  <option value="viewer">Viewer</option>
                  <option value="reviewer">Reviewer</option>
                  <option value="editor">Editor</option>
                </select>
                <button
                  type="submit"
                  disabled={isSharing}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UserPlusIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {getPermissionDescription(selectedPermission)}
              </p>
            </form>

            {/* Shared users list */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Shared with</h4>
              {isLoading ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">Loading...</div>
              ) : shares.length === 0 ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No one has access to this document yet
                </div>
              ) : (
                <div className="space-y-2">
                  {shares.map((share) => (
                    <div
                      key={share.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-md"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {share.shared_with_email}
                          </p>
                          {share.is_pending && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              Pending
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {getPermissionDescription(share.permission_level)}
                        </p>
                        {share.shared_by_username && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            Shared by {share.shared_by_username}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <select
                          value={share.permission_level}
                          onChange={(e) =>
                            handleUpdatePermission(share.id, e.target.value as PermissionLevel)
                          }
                          className="text-xs px-2 py-1 border border-gray-300 dark:border-slate-600 rounded focus:outline-none focus:ring-primary-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="reviewer">Reviewer</option>
                          <option value="editor">Editor</option>
                        </select>
                        {share.is_pending && (
                          <button
                            onClick={() => handleSendReminder(share.id)}
                            className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                            title="Send reminder email"
                          >
                            <EnvelopeIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveShare(share.id)}
                          className="p-1 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          title="Remove share"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-slate-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShareDocumentModal

